import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown, Ticket } from "lucide-react";
import { useI18n } from "../i18n";

const HERO_IMG = "/hero/team-astana-tour-2026.jpg";

const MARQUEE_CITIES = [
  "TASHKENT", "BISHKEK", "DÜSSELDORF", "FRANKFURT", "MÜNCHEN",
  "PADERBORN", "HAMBURG", "BERLIN", "VANCOUVER", "SEATTLE",
  "LOS ANGELES", "CHICAGO", "TORONTO", "MONTREAL", "MIAMI",
  "ORLANDO", "WASHINGTON DC", "ATLANTA", "NEW YORK", "PHILADELPHIA",
];

const Hero = () => {
  const { t } = useI18n();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex flex-col"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 30% 30%, #d41525 0%, #a8101c 45%, #5a0810 80%, #1a0205 100%)",
      }}
    >
      {/* Grain / noise texture */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.09] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* Subtle outline-text city watermark on left */}
      <div
        aria-hidden
        className="hidden md:block absolute -left-10 top-1/4 text-[14vw] leading-none font-display tracking-tighter outline-text opacity-[0.08] pointer-events-none select-none rotate-[-4deg]"
      >
        2026
      </div>
      {/* Bottom fade for marquee contrast */}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-black pointer-events-none" />

      {/* Content grid */}
      <div className="relative z-10 flex-1 flex flex-col">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-28 md:pt-32 pb-14 flex-1 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center min-h-[calc(100vh-200px)]">
            {/* LEFT — text */}
            <div className="lg:col-span-6 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="kicker mb-6"
                data-testid="hero-kicker"
              >
                {t.hero.kicker}
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="font-display text-[18vw] sm:text-[14vw] lg:text-[8.5vw] leading-[0.85] tracking-tighter"
                data-testid="hero-title"
              >
                <span className="block text-white">{t.hero.title1}</span>
                <span className="block text-[#FFDE00]" style={{ textShadow: "0 4px 24px rgba(0,0,0,0.35)" }}>
                  {t.hero.title2}
                </span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="mt-5 flex items-center gap-3"
              >
                <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-semibold text-black bg-[#FFDE00] px-3 py-1.5">
                  USA &amp; CANADA · 2026
                </span>
                <span className="text-[11px] tracking-[0.3em] uppercase text-white/70">
                  Second North America Tour
                </span>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.45 }}
                className="mt-7 max-w-xl text-base md:text-lg text-white/85 font-light leading-relaxed"
                data-testid="hero-subtitle"
              >
                {t.hero.subtitle}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <a href="#tour" className="btn-primary" data-testid="hero-cta-tickets">
                  <Ticket size={16} /> {t.hero.cta_primary}
                </a>
                <a href="#tour" className="btn-outline" data-testid="hero-cta-schedule">
                  {t.hero.cta_secondary}
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="hidden md:flex items-center gap-2 mt-12 text-xs tracking-[0.3em] text-white/55"
              >
                <ArrowDown size={14} /> {t.hero.scroll}
              </motion.div>
            </div>

            {/* RIGHT — poster */}
            <div className="lg:col-span-6 order-1 lg:order-2 relative flex justify-center lg:justify-end">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
                className="relative w-full max-w-[520px]"
              >
                {/* Yellow + black shadow stack behind poster */}
                <div
                  aria-hidden
                  className="absolute -inset-3 sm:-inset-4 bg-[#FFDE00] translate-x-2 translate-y-2 sm:translate-x-3 sm:translate-y-3"
                />
                <div
                  aria-hidden
                  className="absolute -inset-3 sm:-inset-4 bg-black/50 -translate-x-2 -translate-y-2 sm:-translate-x-3 sm:-translate-y-3 backdrop-blur-sm"
                />
                <img
                  src={HERO_IMG}
                  alt="Team Astana — World Tour 2026, USA & Canada"
                  className="relative w-full h-auto block shadow-2xl"
                  loading="eager"
                  data-testid="hero-poster"
                />
                {/* Corner accent */}
                <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 bg-black border border-[#FFDE00]/70 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-semibold text-[#FFDE00]">
                  Official Poster
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative border-t border-white/10 border-b py-5 backdrop-blur-sm bg-black/55">
          <Marquee speed={60} gradient={false} pauseOnHover>
            {MARQUEE_CITIES.concat(MARQUEE_CITIES).map((city, i) => (
              <span
                key={i}
                className="font-display text-4xl md:text-6xl mx-8 outline-text"
              >
                {city} <span className="text-[#FFDE00]">★</span>
              </span>
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
};

export default Hero;
